import { Button, Col, Drawer, Form, Input, Row } from 'antd';
import { Formik } from 'formik';
import { FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import type { DistributionListDrawerProps } from '../../DistributionLists.types.ts';
import {
  DISTRIBUTION_LIST_FORM,
  DISTRIBUTION_LISTS_FORM_SCHEMA,
  DISTRIBUTION_LISTS_INITIAL_FORM_VALUES,
} from '../../DistributionLists.constants.tsx';

import './DistributionListDrawer.styles.css';
import { useTranslation } from 'react-i18next';
import { getDistributionListValues } from '../../DistributionList.helpers.ts';
import { useService } from '../../DistributionLists.hooks.ts';

export default function DistributionListDrawer({
  onClose,
  open,
  onSubmit,
  distributionListId,
}: DistributionListDrawerProps) {
  const { t } = useTranslation();
  const { list } = useService({ distributionListId });

  const initialValues = distributionListId
    ? getDistributionListValues(list)
    : DISTRIBUTION_LISTS_INITIAL_FORM_VALUES;

  return (
    <Drawer
      title={distributionListId ? list?.name : t('pages.distribution-lists.create')}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={DISTRIBUTION_LIST_FORM}
            >
              {t('common.confirm')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={DISTRIBUTION_LISTS_FORM_SCHEMA}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched }) => {
          return (
            <Form
              layout="vertical"
              onFinish={handleSubmit}
              id={DISTRIBUTION_LIST_FORM}
              autoComplete="off"
            >
              <Form.Item
                label={t('form.field.name')}
                validateStatus={touched.name && errors.name ? 'error' : ''}
                help={touched.name && typeof errors.name === 'string' ? errors.name : undefined}
              >
                <Input
                  name="name"
                  variant="underlined"
                  defaultValue={values.name}
                  value={values.name}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.recipients')}
                validateStatus={touched.recipients && errors.recipients ? 'error' : ''}
                help={
                  touched.recipients && typeof errors.recipients === 'string'
                    ? errors.recipients
                    : undefined
                }
              >
                <Input
                  name="recipients"
                  variant="underlined"
                  value={values.recipients}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.select-here')}
                />
              </Form.Item>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
